import { useEffect, useState } from "react";
import { Loader2, Minus, Sparkles, ThumbsDown, ThumbsUp } from "lucide-react";
import { usePlatformApi } from "../../hooks/usePlatformApi";
import { FEEDBACK_STATUSES, type FeedbackRecord, type FeedbackStatus } from "../../types/feedback";

const TRIAGE_NOTES_MAX_LENGTH = 2_000;

interface Props {
  record: FeedbackRecord | null;
  /** The URL-bound feedbackId (may differ from `record` when the
   *  deep-linked record isn't in the current filter window). Used to
   *  distinguish "no selection" from "selection not found". */
  selectedId?: string | null;
  /** Optional callback fired with the server's updated record after a
   *  successful triage save. Parent uses it to splice the row in place. */
  onTriaged?: (updated: FeedbackRecord) => void;
}

export function FeedbackDetailPane({ record, selectedId, onTriaged }: Props) {
  if (!record) {
    // Deep-link miss: URL points to a feedbackId but no matching row in
    // the current filter window. Tell the user *why* the pane is empty.
    if (selectedId) {
      return (
        <div className="flex h-full flex-col items-center justify-center rounded-md border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          <span className="font-mono text-xs text-slate-400">{selectedId}</span>
          <span className="mt-2">
            Record not in current view. Try clearing filters; older records
            may be past the page limit.
          </span>
        </div>
      );
    }
    return (
      <div className="flex h-full items-center justify-center rounded-md border border-dashed border-slate-200 p-6 text-sm text-slate-400 dark:border-slate-700">
        Select a feedback record to view details.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto rounded-md border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <header className="flex items-start gap-3">
        <PolarityIcon polarity={record.polarity} />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-mono text-slate-400 dark:text-slate-500">
            {record.feedbackId}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
            <SurfaceBadge surface={record.surface} />
            <StatusBadge status={record.status} />
            <span className="text-slate-400 dark:text-slate-500">
              {new Date(record.createdAt).toLocaleString()}
            </span>
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            org <span className="font-mono">{record.orgId}</span> · user{" "}
            <span className="font-mono">{record.userId}</span>
          </div>
        </div>
      </header>

      {record.comment && (
        <section>
          <h3 className="mb-1 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Comment
          </h3>
          <p className="whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-sm dark:bg-slate-800">
            {record.comment}
          </p>
        </section>
      )}

      {record.aiEvaluation && (
        <section className="rounded-md border border-indigo-200 bg-indigo-50 p-3 dark:border-indigo-900 dark:bg-indigo-950">
          <h3 className="mb-2 flex items-center gap-1 text-xs font-medium uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
            <Sparkles className="h-3 w-3" /> AI Evaluation
          </h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <KV label="Category" value={record.aiEvaluation.category} />
            <KV label="Sentiment" value={record.aiEvaluation.sentiment} />
            <KV label="Actionable" value={String(record.aiEvaluation.actionable)} />
            <KV label="Model" value={record.aiEvaluation.model} />
          </div>
          {record.aiEvaluation.summary && (
            <p className="mt-2 text-sm italic text-indigo-900 dark:text-indigo-200">
              "{record.aiEvaluation.summary}"
            </p>
          )}
          {record.aiEvaluation.keyThemes.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {record.aiEvaluation.keyThemes.map((t) => (
                <span
                  key={t}
                  className="rounded bg-indigo-200 px-2 py-0.5 text-xs text-indigo-800 dark:bg-indigo-800 dark:text-indigo-100"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      <section>
        <h3 className="mb-1 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Surface context
        </h3>
        <pre className="overflow-x-auto rounded-md bg-slate-50 p-3 text-xs dark:bg-slate-800">
          {JSON.stringify(record.surfaceContext, null, 2)}
        </pre>
      </section>

      <section>
        <h3 className="mb-1 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Client meta
        </h3>
        <pre className="overflow-x-auto rounded-md bg-slate-50 p-3 text-xs dark:bg-slate-800">
          {JSON.stringify(record.clientMeta, null, 2)}
        </pre>
      </section>

      {onTriaged && <TriagePanel record={record} onTriaged={onTriaged} />}
    </div>
  );
}

interface TriageProps {
  record: FeedbackRecord;
  onTriaged: (updated: FeedbackRecord) => void;
}

/**
 * Phase D triage controls. Status dropdown + notes textarea + Save.
 * Local draft state resets when the selected record changes (keyed off
 * `record.feedbackId`). Save is disabled when nothing changed.
 */
function TriagePanel({ record, onTriaged }: TriageProps) {
  const api = usePlatformApi();
  const [status, setStatus] = useState<FeedbackStatus>(record.status);
  const [notes, setNotes] = useState(record.triageNotes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStatus(record.status);
    setNotes(record.triageNotes ?? "");
    setError(null);
  }, [record.feedbackId, record.status, record.triageNotes]);

  const dirty =
    status !== record.status || notes !== (record.triageNotes ?? "");

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const body: { status?: FeedbackStatus; triageNotes?: string } = {};
      if (status !== record.status) body.status = status;
      // Send notes whenever they differ from the stored value, including
      // empty (server treats absent and empty equivalently per Zod).
      if (notes !== (record.triageNotes ?? "")) body.triageNotes = notes;
      const res = await api.updateOrgFeedback(record.orgId, record.feedbackId, body);
      onTriaged(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-amber-700 dark:text-amber-300">
        Triage
      </h3>

      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600 dark:text-slate-300">
          Status
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as FeedbackStatus)}
            disabled={saving}
            className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800"
          >
            {FEEDBACK_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600 dark:text-slate-300">
          Notes <span className="text-slate-400">({notes.length}/{TRIAGE_NOTES_MAX_LENGTH})</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={TRIAGE_NOTES_MAX_LENGTH}
            disabled={saving}
            rows={3}
            placeholder="Add a note explaining the decision (optional)…"
            className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800"
          />
        </label>

        {record.triagedBy && record.triagedAt && (
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Last triaged by <span className="font-mono">{record.triagedBy}</span> on{" "}
            {new Date(record.triagedAt).toLocaleString()}
          </div>
        )}

        {error && (
          <div className="rounded bg-red-50 p-2 text-xs text-red-700 dark:bg-red-950 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save triage
          </button>
        </div>
      </div>
    </section>
  );
}

export function PolarityIcon({
  polarity,
  className,
}: {
  polarity: FeedbackRecord["polarity"];
  className?: string;
}) {
  const cls = className ?? "h-5 w-5";
  if (polarity === "positive") return <ThumbsUp className={`${cls} text-emerald-500`} />;
  if (polarity === "negative") return <ThumbsDown className={`${cls} text-rose-500`} />;
  return <Minus className={`${cls} text-slate-400`} />;
}

function SurfaceBadge({ surface }: { surface: string }) {
  return (
    <span className="rounded bg-slate-200 px-2 py-0.5 font-mono text-xs text-slate-700 dark:bg-slate-700 dark:text-slate-200">
      {surface}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === "open"
      ? "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100"
      : status === "triaged"
        ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100"
        : status === "resolved"
          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100"
          : "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${tone}`}>{status}</span>;
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium text-indigo-600 dark:text-indigo-400">{label}</div>
      <div className="font-mono text-sm text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}
